import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Zap, Users, Target, TrendingUp, AlertTriangle, CheckCircle,
  Swords, BarChart3, Clock, FileText, XCircle, RefreshCw, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

const STATUS_CONFIG = {
  active: { label: 'Active', className: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  cards_only: { label: 'Cards Only', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  leads_only: { label: 'No Cards', className: 'text-red-600 bg-red-50 border-red-200' },
  inactive: { label: 'Inactive', className: 'text-slate-400 bg-slate-50 border-slate-200' },
};

const COMP_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff'];

function StatCard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="transition-all duration-200 hover:shadow-md">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
          {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SalesAssistance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const loadDashboard = () => {
    setLoading(true);
    axios.get(`${API}/sales-assistant/dashboard`, { withCredentials: true })
      .then(res => { setData(res.data); setError(null); })
      .catch(err => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDashboard(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sales-assistant/sync`, {}, { withCredentials: true });
      if (res.data.success) {
        const newCount = res.data.new_battlecards || 0;
        toast.success(newCount > 0 ? `Synced! ${newCount} new battlecards added (${res.data.battlecards} total)` : `Up to date — ${res.data.battlecards} battlecards`);
        loadDashboard();
      } else {
        toast.error(res.data.message || 'Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return (
    <div className="space-y-5" data-testid="sales-assistance-loading">
      <Skeleton className="h-8 w-72" />
      <div className="grid grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      <Skeleton className="h-[400px]" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20" data-testid="sales-assistance-error">
      <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
      <p className="text-slate-500">{error}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={loadDashboard}>
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
      </Button>
    </div>
  );

  const { summary, employee_comparison, kva_gap, segment_analysis, competitor_frequency, recent_battlecards, calc_analytics } = data;
  const saConnected = data.sa_connected !== false;
  const hasData = summary && summary.total_battlecards > 0;

  return (
    <div className="space-y-6" data-testid="sales-assistance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-500" />
            Sales Assistance Intelligence
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">Cross-reference between Sales Battlecard AI and LMS leads</p>
        </div>
        <div className="flex items-center gap-2.5">
          {data.last_synced && (
            <span className="text-[11px] text-slate-400">
              Last synced {new Date(data.last_synced).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} | Auto-syncs every 3h
            </span>
          )}
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-sa-btn" className="h-8 text-xs">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Data'}
          </Button>
          <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${saConnected ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
            <Zap className="h-2.5 w-2.5 mr-1" /> {saConnected ? 'Live' : 'Offline'}
          </Badge>
        </div>
      </div>

      {!saConnected && !hasData && (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600">No cached data available</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Click "Sync Data" above to pull data from the Sales Battlecard AI app</p>
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-sa-empty-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Data Now'}
            </Button>
          </CardContent>
        </Card>
      )}

      {!saConnected && hasData && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Showing cached data. Click "Sync Data" to refresh from the live API.</span>
        </div>
      )}

      {hasData && (<>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Battlecards" value={summary.total_battlecards} icon={FileText} color="bg-indigo-50 text-indigo-600" />
        <StatCard label="Calculations" value={summary.total_calculations} icon={Target} color="bg-violet-50 text-violet-600" />
        <StatCard label="Active Reps" value={summary.active_reps} subtitle="using both tools" icon={CheckCircle} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Cards Only" value={summary.cards_only_reps} subtitle="not logging leads" icon={AlertTriangle} color="bg-amber-50 text-amber-600" />
        <StatCard label="No Cards" value={summary.leads_only_reps} subtitle="leads w/o preparation" icon={XCircle} color="bg-red-50 text-red-500" />
      </div>

      {/* Employee Comparison */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-500" />
            <CardTitle className="text-base font-semibold">Employee Activity Comparison</CardTitle>
          </div>
          <p className="text-xs text-slate-400">Employees with 20+ leads or any battlecard activity</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[380px] overflow-y-auto">
            <Table data-testid="employee-comparison-table">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-[11px]">Employee</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Status</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Battlecards</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">BC Avg KVA</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Leads</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Open</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Won</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-center text-[11px]">Lead Avg KVA</TableHead>
                  <TableHead className="sticky top-0 bg-slate-50 z-10 text-[11px]">Segments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employee_comparison
                  .filter(e => e.battlecards > 0 || e.total_leads >= 20)
                  .map(emp => {
                    const cfg = STATUS_CONFIG[emp.status] || STATUS_CONFIG.inactive;
                    return (
                      <TableRow key={emp.name} className="hover:bg-slate-50/80 transition-colors">
                        <TableCell className="font-medium text-sm">{emp.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${cfg.className}`}>{cfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-center font-semibold text-indigo-600 text-sm">{emp.battlecards || <span className="text-slate-300 font-normal">-</span>}</TableCell>
                        <TableCell className="text-center text-sm text-slate-500">{emp.avg_bc_kva || '-'}</TableCell>
                        <TableCell className="text-center font-medium text-sm">{emp.total_leads || '-'}</TableCell>
                        <TableCell className="text-center text-emerald-600 text-sm">{emp.open_leads || '-'}</TableCell>
                        <TableCell className="text-center text-amber-600 text-sm">{emp.won_leads || '-'}</TableCell>
                        <TableCell className="text-center text-sm text-slate-500">{emp.avg_lead_kva || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {emp.segments.slice(0, 2).map(s => (
                              <span key={s} className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{s}</span>
                            ))}
                            {emp.segments.length > 2 && <span className="text-[10px] text-slate-400">+{emp.segments.length - 2}</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Two column: KVA Gap + Competitors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-violet-500" />
              <CardTitle className="text-base font-semibold">KVA Coverage Gap</CardTitle>
            </div>
            <p className="text-xs text-slate-400">Battlecards vs open leads by KVA range</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {kva_gap.map(row => {
              const max = Math.max(row.battlecards, row.open_leads, 1);
              return (
                <div key={row.kva_range}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-sm font-medium">{row.kva_range} KVA</span>
                    <span className="text-[11px] text-slate-400">{row.battlecards} cards / {row.open_leads} leads</span>
                  </div>
                  <div className="flex gap-1.5 h-2">
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <div className="bg-indigo-400 rounded-full transition-all" style={{ width: `${(row.battlecards / max) * 100}%`, minWidth: row.battlecards ? 6 : 0 }} />
                    </TooltipTrigger><TooltipContent>{row.battlecards} battlecards</TooltipContent></Tooltip></TooltipProvider>
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <div className="bg-emerald-400 rounded-full transition-all" style={{ width: `${(row.open_leads / max) * 100}%`, minWidth: row.open_leads ? 6 : 0 }} />
                    </TooltipTrigger><TooltipContent>{row.open_leads} open leads</TooltipContent></Tooltip></TooltipProvider>
                  </div>
                </div>
              );
            })}
            <div className="flex gap-5 pt-2 border-t text-[11px] text-slate-400">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />Battlecards</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />Open Leads</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Swords className="h-4 w-4 text-indigo-500" />
              <CardTitle className="text-base font-semibold">Competitor Intelligence</CardTitle>
            </div>
            <p className="text-xs text-slate-400">Most targeted competitors in battlecards</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {competitor_frequency.map((comp, i) => {
              const maxC = competitor_frequency[0]?.count || 1;
              const pct = (comp.count / maxC) * 100;
              const barColor = COMP_COLORS[Math.min(i, COMP_COLORS.length - 1)];
              return (
                <div key={comp.name} className="flex items-center gap-3">
                  <span className="text-sm w-24 truncate font-medium text-slate-700">{comp.name}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden relative">
                    <div
                      className="h-full rounded transition-all"
                      style={{ width: `${pct}%`, backgroundColor: barColor }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 w-8 text-right">{comp.count}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Two column: Segments + DG Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-amber-500" />
              <CardTitle className="text-base font-semibold">Segment Analysis</CardTitle>
            </div>
            <p className="text-xs text-slate-400">Battlecard segments vs LMS customer types</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-0 divide-y divide-slate-100">
              {segment_analysis.filter(s => s.battlecards > 0 || s.open_leads > 0).slice(0, 10).map(seg => (
                <div key={seg.segment} className="flex items-center justify-between py-2.5">
                  <span className="text-sm truncate max-w-[220px] text-slate-700">{seg.segment}</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 min-w-[70px] justify-end">
                      <span className="w-2 h-2 rounded-full bg-indigo-400" />
                      <span className="text-xs font-medium text-slate-600">{seg.battlecards}</span>
                    </div>
                    <div className="flex items-center gap-1.5 min-w-[70px] justify-end">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-xs font-medium text-slate-600">{seg.open_leads}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-5 pt-3 mt-2 border-t border-slate-100 text-[10px] text-slate-400">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-indigo-400" />Cards</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" />Leads</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-violet-500" />
              <CardTitle className="text-base font-semibold">DG Recommendation Trends</CardTitle>
            </div>
            <p className="text-xs text-slate-400">Most suggested ratings from Load Calculator</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {(calc_analytics?.top_suggested_kva || []).filter(k => k.kva).slice(0, 8).map((item, idx) => {
              const maxC = (calc_analytics?.top_suggested_kva || [])[0]?.count || 1;
              return (
                <div key={item.kva} className="flex items-center gap-3">
                  <Badge variant="outline" className="w-20 justify-center font-mono text-xs bg-violet-50 text-violet-700 border-violet-200">{item.kva} KVA</Badge>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-400 rounded-full transition-all" style={{ width: `${(item.count / maxC) * 100}%` }} />
                  </div>
                  <span className="text-xs text-slate-500 w-14 text-right">{item.count} times</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Recent Battlecards */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            <CardTitle className="text-base font-semibold">Recent Battlecards</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="recent-battlecards-table">
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="text-[11px]">Sales Rep</TableHead>
                <TableHead className="text-[11px]">Segment</TableHead>
                <TableHead className="text-center text-[11px]">KVA</TableHead>
                <TableHead className="text-[11px]">Competitors</TableHead>
                <TableHead className="text-[11px]">Model</TableHead>
                <TableHead className="text-[11px]">Generated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent_battlecards.map((bc, i) => (
                <TableRow key={i} className="hover:bg-slate-50/80 transition-colors">
                  <TableCell>
                    <div className="font-medium text-sm">{bc.user}</div>
                    {bc.role && <div className="text-[10px] text-slate-400">{bc.role}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{bc.segment}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="font-mono text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{bc.kva}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {bc.competitors.map(c => (
                        <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{c}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{bc.model || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {bc.generated_at ? new Date(bc.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>)}
    </div>
  );
}
